import React, { useState, useEffect } from 'react';
import { Mail, MapPin, Send, Copy, Check, ExternalLink, ShieldCheck, AlertCircle, RefreshCw } from 'lucide-react';
import { portfolioData } from '../data/portfolioData';
import { LinkedinIcon, GithubIcon, WhatsappIcon } from './SocialIcons';

export const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    whatsapp: '',
    subject: '',
    message: '',
  });

  // Anti-Bot & Anti-Spam Security States
  const [honeypot, setHoneypot] = useState('');
  const [userMathAnswer, setUserMathAnswer] = useState('');
  const [captchaNum1, setCaptchaNum1] = useState(5);
  const [captchaNum2, setCaptchaNum2] = useState(3);
  const [securityError, setSecurityError] = useState('');

  const [copiedEmail, setCopiedEmail] = useState(false);
  const [copiedWhatsapp, setCopiedWhatsapp] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [cooldownTime, setCooldownTime] = useState(0);

  const googleFormUrl =
    'https://docs.google.com/forms/d/e/1FAIpQLSd347Uf5tjiBMNRZ_5JGMEqGiLkdijbsxDZJ3lLG2nkmYItEw/viewform?usp=sf_link';
  const googleFormPostUrl =
    'https://docs.google.com/forms/d/e/1FAIpQLSd347Uf5tjiBMNRZ_5JGMEqGiLkdijbsxDZJ3lLG2nkmYItEw/formResponse';

  const generateCaptcha = () => {
    const n1 = Math.floor(Math.random() * 9) + 1;
    const n2 = Math.floor(Math.random() * 9) + 1;
    setCaptchaNum1(n1);
    setCaptchaNum2(n2);
    setUserMathAnswer('');
  };

  useEffect(() => {
    generateCaptcha();
  }, []);

  useEffect(() => {
    if (cooldownTime > 0) {
      const timer = setInterval(() => {
        setCooldownTime((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [cooldownTime]);

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(portfolioData.email);
    setCopiedEmail(true);
    setTimeout(() => setCopiedEmail(false), 2000);
  };

  const handleCopyWhatsapp = () => {
    navigator.clipboard.writeText(portfolioData.whatsappUsername);
    setCopiedWhatsapp(true);
    setTimeout(() => setCopiedWhatsapp(false), 2000);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSecurityError('');

    // 1. Honeypot Check: If invisible field is populated, silently reject bot submission
    if (honeypot.trim().length > 0) {
      setFormSubmitted(true);
      return;
    }

    // 2. Rate-Limiting Cooldown Check
    const lastSubmitTime = localStorage.getItem('portfolio_last_submit');
    const now = Date.now();
    if (lastSubmitTime && now - parseInt(lastSubmitTime, 10) < 60000) {
      const remainingSecs = Math.ceil((60000 - (now - parseInt(lastSubmitTime, 10))) / 1000);
      setCooldownTime(remainingSecs);
      setSecurityError(`Rate limit reached. Please wait ${remainingSecs}s before sending another message.`);
      return;
    }

    // 3. Email Format & Message Content Security Check
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setSecurityError('Please provide a valid email address.');
      return;
    }

    if (formData.message.trim().length < 10) {
      setSecurityError('Message is too short. Please provide at least 10 characters.');
      return;
    }

    // 4. Math Security Challenge Check
    const expectedAnswer = captchaNum1 + captchaNum2;
    if (parseInt(userMathAnswer, 10) !== expectedAnswer) {
      setSecurityError(`Incorrect security verification answer. Please solve ${captchaNum1} + ${captchaNum2}.`);
      generateCaptcha();
      return;
    }

    setIsSubmitting(true);

    const formParams = new URLSearchParams();
    formParams.append('entry.2005620554', formData.name);
    formParams.append('entry.1045781291', formData.email);
    formParams.append('entry.1166974658', formData.whatsapp || portfolioData.whatsappUsername);
    formParams.append('entry.1332530073', formData.subject || 'Portfolio Inquiry');
    formParams.append('entry.839337160', formData.message);
    formParams.append('fvv', '1');
    formParams.append('pageHistory', '0');

    try {
      await fetch(googleFormPostUrl, {
        method: 'POST',
        mode: 'no-cors',
        body: formParams,
      });
    } catch (err) {
      console.log('Submitted to Google Form');
    }

    localStorage.setItem('portfolio_last_submit', Date.now().toString());

    setIsSubmitting(false);
    setFormSubmitted(true);
    generateCaptcha();

    setTimeout(() => {
      setFormSubmitted(false);
      setFormData({ name: '', email: '', whatsapp: '', subject: '', message: '' });
    }, 6000);
  };

  return (
    <section id="contact" className="section contact-section">
      <div className="container">
        <div className="section-header">
          <div className="section-badge">
            <Mail size={14} />
            <span>Get In Touch</span>
          </div>
          <h2 className="section-title">
            Let's Build <span className="text-gradient">Something Great</span>
          </h2>
          <p className="section-subtitle">
            Whether you need architectural advice, full-stack development, or team leadership — send a direct message below.
          </p>
        </div>

        <div className="contact-grid">
          {/* Left: Quick Contact Info */}
          <div className="contact-info">
            <div className="info-card glass-card">
              <div className="info-icon">
                <Mail size={22} />
              </div>
              <div className="info-details">
                <span className="info-label">Direct Email</span>
                <a href={`mailto:${portfolioData.email}`} className="info-value">
                  {portfolioData.email}
                </a>
              </div>
              <button className="copy-btn" onClick={handleCopyEmail} title="Copy Email">
                {copiedEmail ? <Check size={16} className="text-success" /> : <Copy size={16} />}
              </button>
            </div>

            <div className="info-card glass-card">
              <div className="info-icon">
                <WhatsappIcon size={22} />
              </div>
              <div className="info-details">
                <span className="info-label">WhatsApp</span>
                <a
                  href={portfolioData.whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="info-value"
                >
                  @{portfolioData.whatsappUsername}
                </a>
              </div>
              <button className="copy-btn" onClick={handleCopyWhatsapp} title="Copy WhatsApp Username">
                {copiedWhatsapp ? <Check size={16} className="text-success" /> : <Copy size={16} />}
              </button>
            </div>

            <div className="info-card glass-card">
              <div className="info-icon">
                <MapPin size={22} />
              </div>
              <div className="info-details">
                <span className="info-label">Location</span>
                <span className="info-value">{portfolioData.address}</span>
              </div>
            </div>

            <div className="social-card glass-card">
              <div className="social-card-header">
                <h4>Social Profiles</h4>
                <a
                  href={googleFormUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="gform-link"
                >
                  <span>Google Form</span>
                  <ExternalLink size={14} />
                </a>
              </div>
              <div className="social-links">
                <a
                  href={portfolioData.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-btn"
                >
                  <LinkedinIcon size={20} />
                  <span>LinkedIn</span>
                </a>
                {portfolioData.github && (
                  <a
                    href={portfolioData.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-btn"
                  >
                    <GithubIcon size={20} />
                    <span>GitHub</span>
                  </a>
                )}
                {portfolioData.whatsappUrl && (
                  <a
                    href={portfolioData.whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-btn"
                  >
                    <WhatsappIcon size={20} />
                    <span>WhatsApp</span>
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Right: Secured Contact Form */}
          <div className="contact-form-wrapper glass-card">
            <div className="form-header-row">
              <h3 className="form-title">Send a Message</h3>
              <span className="shield-badge">
                <ShieldCheck size={14} /> Bot-Protected
              </span>
            </div>

            {formSubmitted ? (
              <div className="form-success">
                <Check size={48} className="success-icon" />
                <h4>Message Received & Recorded!</h4>
                <p>Thank you for reaching out. Your response has been saved directly to Inder's contact database. He will respond within 24 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="contact-form">
                {/* Security Error Alert */}
                {securityError && (
                  <div className="security-alert">
                    <AlertCircle size={18} />
                    <span>{securityError}</span>
                  </div>
                )}

                {/* Honeypot Input (Visually hidden for bot protection) */}
                <div style={{ display: 'none', position: 'absolute', left: '-9999px' }} aria-hidden="true">
                  <input
                    type="text"
                    name="website_honeypot_field"
                    tabIndex={-1}
                    autoComplete="off"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Your Name *</label>
                    <input
                      type="text"
                      id="name"
                      required
                      placeholder="e.g. Sarah Jenkins"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Your Email *</label>
                    <input
                      type="email"
                      id="email"
                      required
                      placeholder="e.g. sarah@company.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="whatsapp">WhatsApp Username / Contact (Optional)</label>
                    <input
                      type="text"
                      id="whatsapp"
                      placeholder={`e.g. @${portfolioData.whatsappUsername}`}
                      value={formData.whatsapp}
                      onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="subject">Subject</label>
                    <input
                      type="text"
                      id="subject"
                      placeholder="Project Inquiry / Consultation"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    rows={4}
                    required
                    placeholder="Tell me about your project scope, timeline, and requirements..."
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  ></textarea>
                </div>

                {/* Anti-Spam Security Challenge */}
                <div className="captcha-box glass-card">
                  <div className="captcha-label">
                    <ShieldCheck size={16} className="captcha-icon" />
                    <span>Security Check: What is {captchaNum1} + {captchaNum2}? *</span>
                  </div>
                  <div className="captcha-input-row">
                    <input
                      type="number"
                      required
                      placeholder="Answer"
                      className="captcha-input"
                      value={userMathAnswer}
                      onChange={(e) => setUserMathAnswer(e.target.value)}
                    />
                    <button
                      type="button"
                      className="refresh-captcha-btn"
                      onClick={generateCaptcha}
                      title="Generate new question"
                    >
                      <RefreshCw size={14} />
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || cooldownTime > 0}
                  className="btn btn-primary submit-btn"
                >
                  <Send size={18} />
                  <span>
                    {cooldownTime > 0
                      ? `Wait ${cooldownTime}s`
                      : isSubmitting
                      ? 'Securing & Sending...'
                      : 'Send Secured Message'}
                  </span>
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .contact-section {
          background: var(--bg-secondary);
        }

        .contact-grid {
          display: grid;
          grid-template-columns: 0.9fr 1.1fr;
          gap: 2rem;
          align-items: start;
        }

        .contact-info {
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
        }

        .info-card {
          padding: 1.25rem;
          display: flex;
          align-items: center;
          gap: 1rem;
          position: relative;
        }

        .info-icon {
          width: 46px;
          height: 46px;
          border-radius: var(--radius-sm);
          background: var(--gradient-brand);
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .info-details {
          display: flex;
          flex-direction: column;
          flex-grow: 1;
        }

        .info-label {
          font-size: 0.8rem;
          color: var(--text-dim);
          font-weight: 600;
          text-transform: uppercase;
        }

        .info-value {
          font-size: 1.05rem;
          font-weight: 700;
          color: var(--text-main);
          text-decoration: none;
          word-break: break-all;
        }

        .info-value:hover {
          color: var(--accent-primary);
        }

        .copy-btn {
          background: var(--bg-tertiary);
          border: 1px solid var(--border-color);
          color: var(--text-muted);
          width: 36px;
          height: 36px;
          border-radius: var(--radius-sm);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .copy-btn:hover {
          color: var(--accent-primary);
          border-color: var(--border-highlight);
        }

        .text-success {
          color: var(--accent-success);
        }

        .social-card {
          padding: 1.5rem;
        }

        .social-card-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 1rem;
        }

        .social-card-header h4 {
          font-size: 0.95rem;
          color: var(--text-main);
        }

        .gform-link {
          display: flex;
          align-items: center;
          gap: 0.35rem;
          font-size: 0.8rem;
          color: var(--accent-secondary);
          text-decoration: none;
          font-weight: 600;
        }

        .gform-link:hover {
          color: var(--accent-primary);
        }

        .social-links {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .social-btn {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.65rem 1.1rem;
          border-radius: var(--radius-md);
          background: var(--bg-tertiary);
          border: 1px solid var(--border-color);
          color: var(--text-main);
          text-decoration: none;
          font-weight: 600;
          font-size: 0.9rem;
          transition: all 0.2s ease;
        }

        .social-btn:hover {
          color: var(--accent-primary);
          border-color: var(--border-highlight);
          transform: translateY(-2px);
        }

        .contact-form-wrapper {
          padding: 2.5rem;
        }

        .form-header-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 1.5rem;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .form-title {
          font-size: 1.5rem;
          font-weight: 800;
          color: var(--text-main);
        }

        .shield-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          padding: 0.3rem 0.75rem;
          border-radius: var(--radius-full);
          background: rgba(16, 185, 129, 0.12);
          border: 1px solid rgba(16, 185, 129, 0.3);
          color: var(--accent-success);
          font-size: 0.8rem;
          font-weight: 600;
        }

        .security-alert {
          display: flex;
          align-items: center;
          gap: 0.6rem;
          padding: 0.75rem 1rem;
          border-radius: var(--radius-md);
          background: rgba(239, 68, 68, 0.12);
          border: 1px solid rgba(239, 68, 68, 0.3);
          color: #ef4444;
          font-size: 0.88rem;
          font-weight: 600;
        }

        .contact-form {
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
        }

        .form-row {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1.25rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.4rem;
        }

        .form-group label {
          font-size: 0.85rem;
          font-weight: 600;
          color: var(--text-muted);
        }

        .form-group input,
        .form-group textarea {
          width: 100%;
          padding: 0.85rem 1rem;
          border-radius: var(--radius-md);
          background: var(--bg-primary);
          border: 1px solid var(--border-color);
          color: var(--text-main);
          font-family: inherit;
          font-size: 0.95rem;
          outline: none;
          transition: border-color 0.2s ease;
        }

        .form-group input:focus,
        .form-group textarea:focus {
          border-color: var(--accent-primary);
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
        }

        .captcha-box {
          padding: 1rem 1.25rem;
          background: var(--bg-primary);
          display: flex;
          flex-direction: column;
          gap: 0.6rem;
          border: 1px solid var(--border-highlight);
        }

        .captcha-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.88rem;
          font-weight: 700;
          color: var(--text-main);
        }

        .captcha-icon {
          color: var(--accent-secondary);
        }

        .captcha-input-row {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .captcha-input {
          width: 120px;
          padding: 0.6rem 0.85rem;
          border-radius: var(--radius-sm);
          background: var(--bg-secondary);
          border: 1px solid var(--border-color);
          color: var(--text-main);
          font-family: var(--font-mono);
          font-weight: 700;
          font-size: 1rem;
          outline: none;
        }

        .captcha-input:focus {
          border-color: var(--accent-primary);
        }

        .refresh-captcha-btn {
          background: var(--bg-tertiary);
          border: 1px solid var(--border-color);
          color: var(--text-muted);
          width: 34px;
          height: 34px;
          border-radius: var(--radius-sm);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .refresh-captcha-btn:hover {
          color: var(--accent-primary);
          border-color: var(--border-highlight);
        }

        .submit-btn {
          margin-top: 0.5rem;
          width: 100%;
        }

        .submit-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .form-success {
          text-align: center;
          padding: 3rem 1rem;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .success-icon {
          color: var(--accent-success);
          margin-bottom: 1rem;
        }

        .form-success h4 {
          font-size: 1.4rem;
          color: var(--text-main);
          margin-bottom: 0.5rem;
        }

        .form-success p {
          color: var(--text-muted);
          line-height: 1.6;
        }

        @media (max-width: 992px) {
          .contact-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 576px) {
          .form-row {
            grid-template-columns: 1fr;
          }
          .contact-form-wrapper {
            padding: 1.5rem;
          }
        }
      `}</style>
    </section>
  );
};
